import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import { CascadeStart, Client } from "types";
import ClientPreferences from "../settlement_types/ClientPreferences";
import { useSession } from "providers/SessionProvider";
import { getUserFullName } from "helpers";
import ClientAdditionalItemsPreferences from "./ClientAdditionalItemsPreferences";

type Props = {
  open: boolean;
  handleOpen: () => void;
  value: Client;
  onEdit: (client: Client) => void;
  cascadeStarts: CascadeStart[];
  clients: Client[];
};

export default function EditClientModal({
  open = true,
  handleOpen,
  value,
  onEdit,
  cascadeStarts,
  clients
}: Props) {
  const { users } = useSession();
  const [client, setClient] = useState<Client>(value);
  
  const hasChildClients = useMemo(
    () => clients.some((item) => item.parent_client_id === client.id),
    [clients, client.id],
  );

  const requiresSettlementTypes = !hasChildClients;

  const defaultSettlementTypesCount =
    client.client_settlement_types?.filter((preference) => preference.is_default)
      .length ?? 0;

  const hasRequiredClientFields =
    Boolean(client.razon_social) &&
    Boolean(client.cuit) &&
    Boolean(client.address) &&
    Boolean(client.contact) &&
    Boolean(client.code) &&
    Boolean(client.observations);

  const hasRequiredSettlementTypes =
    !requiresSettlementTypes || defaultSettlementTypesCount === 3;

  const canSave = hasRequiredClientFields && hasRequiredSettlementTypes;
    
  const availableParentClients = useMemo(() => {
    return [...clients]
      .filter(
        (item) =>
          item.is_active &&
          item.parent_client_id == null &&
          item.id !== client.id,
      )
      .sort((a, b) => a.razon_social.localeCompare(b.razon_social));
  }, [clients, client.id]);

  useEffect(() => {
    if (open) {
      setClient(value);
    }
  }, [open, value]);

  const handleChange = <K extends keyof Omit<Client, "id">>(
    name: K,
    value: Omit<Client, "id">[K]
  ) => {
    setClient({
      ...client,
      [name]: value,
    });
  };

  const onSubmit = (updatedClient: Client) => {
    const clientPreferencesList =
      client.client_settlement_types
        ?.filter((clientSettlementType) => clientSettlementType.price > 0)
        .map((clientSettlementType) => ({
          ...clientSettlementType,
          minimum_value:
            clientSettlementType.minimum_value === null ||
            clientSettlementType.minimum_value === undefined ||
            clientSettlementType.minimum_value === ("" as unknown as number)
              ? null
              : Number(clientSettlementType.minimum_value),
        })) ?? [];

  const clientAdditionalItems = client.client_additional_items?.map((item) => ({
      ...item,
      price:
        item.price === null || item.price === undefined
          ? null
          : Number(item.price),
    })) ?? [];

    onEdit({
      ...updatedClient,
      client_settlement_types: clientPreferencesList,
      client_additional_items: clientAdditionalItems,
    });
  };

  useEffect(() => {
    if (open) {
      const fallbackId = cascadeStarts[0]?.id;
      setClient(prev => ({
        ...value,
        cascade_start_id: value.cascade_start_id ?? fallbackId,
      }));
    }
  }, [open, value, cascadeStarts]);

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { sm: "90%", md: "60rem" },
          height: "90%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          overflow: "auto",
        }}
      >
        <Grid container columnSpacing={1}>
          <Typography variant="h5" gutterBottom>
            Editar cliente
          </Typography>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              required
              id="outlined-required"
              label="Razón Social"
              type="text"
              fullWidth
              value={client.razon_social}
              onChange={(e) => handleChange("razon_social", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              id="outlined-required"
              label="Dirección"
              type="text"
              fullWidth
              value={client.address}
              onChange={(e) => handleChange("address", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              id="outlined-required"
              label="CUIT"
              type="text"
              fullWidth
              value={client.cuit}
              onChange={(e) => handleChange("cuit", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              id="outlined-required"
              label="Contacto"
              type="text"
              fullWidth
              value={client.contact}
              onChange={(e) => handleChange("contact", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              id="outlined-required"
              label="Codigo"
              type="text"
              fullWidth
              value={client.code}
              onChange={(e) => handleChange("code", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              id="outlined-required"
              label="Observaciones"
              type="text"
              fullWidth
              value={client.observations}
              onChange={(e) => handleChange("observations", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Criterio de selección de Lote"
              value={client.cascade_start_id ?? cascadeStarts[0]?.id ?? 1}
              onChange={(e) => handleChange("cascade_start_id", Number(e.target.value))}
              disabled={cascadeStarts.length === 0}
            >
              {cascadeStarts.map(cs => (
                <MenuItem key={cs.id} value={cs.id}>
                  {cs.description ?? cs.code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Cliente padre"
              value={client.parent_client_id ?? ""}
              disabled={hasChildClients}
              onChange={(e) =>
                handleChange(
                  "parent_client_id",
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            >
              <MenuItem value="">
                <em>Sin cliente padre</em>
              </MenuItem>

              {availableParentClients.map((parentClient) => (
                <MenuItem key={parentClient.id} value={parentClient.id}>
                  {parentClient.code} - {parentClient.razon_social}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={client.is_active}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                  color="primary"
                />
              }
              label="Cliente activo"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={client.show_lote}
                  onChange={(e) => handleChange("show_lote", e.target.checked)}
                  color="primary"
                />
              }
              label="Mostrar Lote"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={client.show_fecha_vencimiento}
                  onChange={(e) =>
                    handleChange("show_fecha_vencimiento", e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Mostrar Fecha Vencimiento"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={client.show_oc}
                  onChange={(e) => handleChange("show_oc", e.target.checked)}
                  color="primary"
                />
              }
              label="Mostrar OC"
            />
          </Grid>
          {requiresSettlementTypes && (
            <Grid item xs={12}>
              <ClientPreferences client={client} onChange={handleChange} />
            </Grid>
          )}
          <Grid item xs={12}>
            <ClientAdditionalItemsPreferences
              client={client}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label="Creado por"
              value={
                client.created_by
                  ? getUserFullName(client.created_by, users)
                  : "SISTEMA"
              }
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                handleOpen();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!canSave}
              onClick={() => {
                onSubmit(client);
                handleOpen();
              }}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
